'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'

import { IPromotion } from '../../types/schema'

import '@/styles/components/_promotionCard.scss'

//__HELPER__
function isImage() {
  return (src: string) => {
    const ext = src.split('?')[0].split('.').pop()?.toLowerCase()
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '')
  }
}

function isVideo() {
  return (src: string) => {
    const ext = src.split('?')[0].split('.').pop()?.toLowerCase()
    return ['mp4', 'webm', 'ogg', 'mov'].includes(ext || '')
  }
}

// Helper function to format promotion URLs correctly
function formatPromotionUrl(locale: string, landingPage: string | undefined): string {
  if (!landingPage) return ''

  // If the landing page already has a full URL format (with http/https), return it as is
  if (landingPage.startsWith('http')) {
    return landingPage
  }

  // Ensure the URL is always /promotion/{landing_page}
  return `/${locale}/promotion/${landingPage.replace(/^\/+/, '')}`
}

export default function PromotionCard({
  promotions
}: {
  promotions: IPromotion[]
  activeLocation: string
  cityLinks: { name: string; slug: string }[]
  activeSlug: string
  locations: string[]
  cityBranchMap: Record<string, number[]>
}) {
  const t = useTranslations()
  const params = useParams()
  const locale = (params?.locale as string) || 'en'

  return (
    <div className='promotion-cards'>
      {promotions.map((promotion, index) => {
        const mediaUrl = promotion.promotion_img
        const checkIfImage = isImage()
        const checkIfVideo = isVideo()
        const isVideoFile = checkIfVideo(mediaUrl)

        return (
          <div
            className={`promotion-card ${promotion.expired ? 'expired' : ''}`}
            key={promotion.promotion_id}>
            <div className='card-image'>
              {promotion.promotion_lhead && (
                <div className='promotion-ribbon'>
                  <span>{promotion.promotion_lhead}</span>
                </div>
              )}
              <a href={promotion.url} rel='noopener noreferrer'>
                <div className='image-container'>
                  {promotion.expired && (
                    <div className='expired-overlay'>
                      <span className='expired-label'>Expired</span>
                    </div>
                  )}
                  {isVideoFile ? (
                    <video
                      src={mediaUrl}
                      width={500}
                      height={300}
                      preload='metadata'
                      autoPlay
                      loop
                      muted
                      className='promotion-image'
                    />
                  ) : (
                    <img
                      src={mediaUrl}
                      alt={promotion.promotion_rhead}
                      width={500}
                      height={300}
                      loading='lazy'
                      className='promotion-image'
                    />
                  )}
                </div>
              </a>
            </div>
            <div className='card-content'>
              <a href={promotion.url} rel='noopener noreferrer'>
                <h3>{promotion.promotion_rhead}</h3>
              </a>
              <p
                dangerouslySetInnerHTML={{
                  __html: promotion.word_promotioncard || promotion.word_gotpromotion
                }}></p>
              {promotion.expired && (
                <div className='expired-notice'>
                  <small>⚠️ This promotion has expired, but you can still view the details.</small>
                </div>
              )}
              <div className='find-out-more-wrapper'>
                <a href={promotion.url} className='view-deal-button' rel='noopener noreferrer'>
                  {t('Menu.FINDOUTMORE_BUTTON')}&nbsp;⟶
                </a>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
